'use client';

import { ActionsCell } from './components/ActionsCell';
import {
  Checkbox,
  CellEntityType,
  CellYear,
  CellDate,
  CellActions,
  CellContainer,
  CellCheckbox,
  CellUserName,
  Pill,
  CellID,
} from '@/_components';
import { DateCell } from './components/DateCell';
import { DocumentsCell } from './components/DocumentsCell';
import { FirmRoleCell } from './components/FirmRoleCell';
import { FirmCustomStatusCell } from './components/FirmCustomStatusCell';
import { LastActivityCell } from './components/LastActivityCell';
import { NameCell } from './components/NameCell';
import { NextReminderCell } from './components/NextReminderCell';
import { NotesCell } from './components/NotesCell';
import { OverviewCellProps } from './OverviewCell.types';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { StatusCell } from './components/StatusCell';
import { getClasses } from '@/_utils/classnames';
import { type FC, useContext } from 'react';
import { useFirmUser } from '@/_hooks/useFirmUser';
import type { Section } from '@/_types/models/sections';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const OverviewCell: FC<OverviewCellProps> = ({
  isSelected,
  isTop,
  questionnaire,
  isLastColumn,
  setSelectedQuestionnaires,
  toggleQuestionnaireSelection,
  type,
  hasExtraPadding,
}) => {
  const { preferences } = useContext(FirmSettingsContext);
  const {
    setQuestionnairePreview,
    setQuestionnairePreviewOpen,
    setShouldAutoOpenBUPOnPreview,
  } = useContext(QuestionnaireContext);
  const assignedFirmUser = useFirmUser(
    questionnaire?.user_details?.assigned_advisor_id,
  );

  const hasSplitDocumentsToReview: boolean = preferences.bulk_upload_processor
    ? questionnaire.has_split_documents_to_review
    : false;

  const onPreviewQuestionnaire = () => {
    setQuestionnairePreview(questionnaire);
    setQuestionnairePreviewOpen(true);
  };

  const onSelectQuestionnaire = () =>
    toggleQuestionnaireSelection?.(questionnaire);

  const getCellContent = () => {
    switch (type) {
      case 'select': {
        return (
          <CellCheckbox>
            <Checkbox
              label="Select"
              isLabelHidden={true}
              isChecked={isSelected || false}
              onChange={onSelectQuestionnaire}
            />
          </CellCheckbox>
        );
      }
      case 'notes': {
        return (
          <CellContainer width={48}>
            <NotesCell notes={questionnaire.notes} />
          </CellContainer>
        );
      }
      case 'firm_custom_status_id': {
        return (
          <CellContainer width={120}>
            <FirmCustomStatusCell questionnaire={questionnaire} />
          </CellContainer>
        );
      }
      case 'firm_role': {
        return (
          <CellContainer width={160}>
            <FirmRoleCell
              firmRoleAssignments={questionnaire.firm_role_assignments}
            />
          </CellContainer>
        );
      }
      case 'documents': {
        const onOpenReview = () => {
          setQuestionnairePreview(questionnaire);
          setShouldAutoOpenBUPOnPreview(true);
          setQuestionnairePreviewOpen(true);
        };
        return (
          <CellContainer width={52}>
            <DocumentsCell
              allCount={
                questionnaire.upload_question_with_document_count_needed
              }
              doneCount={
                questionnaire.upload_question_with_document_count_uploaded
              }
              documentsNeeded={questionnaire.documents_needed}
              documentsUploaded={questionnaire.documents_uploaded_and_needed}
              hasSplitDocumentsToReview={hasSplitDocumentsToReview}
              onOpenReview={onOpenReview}
            />
          </CellContainer>
        );
      }
      case 'status': {
        const validSections: Array<Section> = questionnaire.sections.filter(
          (s) => s.section_key !== 'document_checklist',
        );
        const sectionCount = validSections.length;
        const sectionDoneCount = validSections.filter(
          (a) => a.marked_completed,
        ).length;

        const currentSection =
          questionnaire.sections &&
          questionnaire.sections.find(
            (section) => section.id === questionnaire.status,
          );
        const currentStep =
          currentSection && (questionnaire.status ?? -1) > 0
            ? currentSection.name
            : 'In Progress';

        const incompleteSections: Array<string> = questionnaire?.sections
          ?.filter((s) => !s.marked_completed && !!s.triggered)
          ?.map((s) => s.name);

        // TODO(API): @chris opened_timestamp returns empty when filtered with `opened` status
        return (
          <CellContainer width={120}>
            <StatusCell
              total={sectionCount}
              count={sectionDoneCount}
              questionnaire={questionnaire}
              currentStep={currentStep}
              incompleteSections={incompleteSections}
            />
          </CellContainer>
        );
      }
      case 'client': {
        return (
          <CellUserName
            companyName={questionnaire?.company_details?.name}
            companyHash={questionnaire?.company_details?.hash_id}
          />
        );
      }
      case 'last_activity':
        return (
          <CellDate>
            <LastActivityCell questionnaire={questionnaire} />
          </CellDate>
        );
      case 'due_date':
        return questionnaire?.completed ? (
          '-'
        ) : (
          <CellDate>
            <DateCell
              isTop={isTop}
              date={questionnaire.due_date}
              isDue={true}
            />
          </CellDate>
        );
      case 'year':
        return <CellYear year={questionnaire.tax_year} />;
      case 'external_user_id':
        return <CellID id={questionnaire?.user_details?.external_user_id} />;
      case 'external_entity_id':
        return (
          <CellID id={questionnaire?.company_details?.external_entity_id} />
        );
      case 'assigned':
        return <CellContainer width={160} text={assignedFirmUser?.name} />;
      case 'entity_type':
        return (
          <CellEntityType
            company={{
              entity_type: questionnaire.company_details.entity_type,
              inactive: questionnaire.company_details.inactive,
            }}
          />
        );
      case 'submitted_at':
        return (
          <CellContainer width={120}>
            <DateCell isTop={isTop} date={questionnaire.submitted_at} />
          </CellContainer>
        );
      case 'first_doc_uploaded_at':
        return (
          <CellContainer width={120}>
            <DateCell
              isTop={isTop}
              date={questionnaire.first_doc_uploaded_at}
            />
          </CellContainer>
        );
      case 'name': {
        return (
          <CellContainer width={160}>
            <NameCell
              onPreview={onPreviewQuestionnaire}
              sentDate={questionnaire.sent_at || ''}
              createdDate={questionnaire.created_at}
              isDraft={questionnaire.is_draft}
              isTracking={questionnaire.track_changes}
              isLocked={questionnaire.locked}
              deletedAt={questionnaire.deleted_at}
              name={
                questionnaire.is_open_item
                  ? `Open items regarding ${questionnaire.name}`
                  : questionnaire.name
              }
            />
          </CellContainer>
        );
      }
      case 'actions': {
        return (
          <CellActions>
            <ActionsCell
              questionnaire={questionnaire}
              setSelectedQuestionnaires={setSelectedQuestionnaires!}
            />
          </CellActions>
        );
      }
      case 'next_reminder_date':
        return (
          <CellContainer width={108}>
            <NextReminderCell questionnaire={questionnaire} />
          </CellContainer>
        );
      case 'firm_group':
        return (
          <CellContainer>
            <Pill
              intent={
                !!questionnaire.user_details?.firm_group_name
                  ? 'secondary'
                  : 'default'
              }
              label={
                questionnaire.user_details?.firm_group_name || 'No firm group'
              }
            />
          </CellContainer>
        );
      default:
        return null;
    }
  };

  const overviewCellClasses: string = getClasses({
    sorabanTable__fixedColumn: isLastColumn,
    extraPadding: hasExtraPadding,
  });

  return <td className={overviewCellClasses}>{getCellContent()}</td>;
};
